import { useState } from 'react';
import { Accordion, Card as BSCard, Dropdown } from 'react-bootstrap';
import PratusSelect, {
  type OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import { AccordionHeader } from 'components/DesignSystems/Accordion/AccordionHeader';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { AddLayerFolderModal } from './AddLayerFolderModal';
import { useUscgMapContext } from './UscgMapContext';
import { ExternalLayerItem } from './ExternalLayerItem';
import { InternalLayerItem } from './InternalLayerItem';
import { LayerDetailModal } from './LayerDetailModal';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import type {
  InternalLayer,
  LayerWithNodeMeta,
} from 'hooks/internalLayer/types';
import { useAors } from 'hooks/useAors';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useDeleteInternalLayerMutation } from 'hooks/internalLayer/useDeleteInternalLayerMutation';
import { useDeleteExternalLayerMutation } from 'hooks/externalLayer/useDeleteExternalLayerMutation';
import { useIsCommandCenter } from './utils/isCommandCenter';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { registerArcGisOAuthInfo } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisPortal';
import { selectGroup, useAppSelector } from 'slices/commonSelectors';
import { IncidentSelect } from './IncidentSelect';
import { ViewLayerFolderModal } from './ViewLayerFolderModal';
import { useListLayerNodes } from 'hooks/layerNode/useListLayerNodes';
import { useDeleteLayerNodeMutation } from 'hooks/layerNode/useDeleteLayerNodeMutation';
import type { LayerGroupDto, LayerFolderItemDto } from 'hooks/layerNode/types';
import { OnAddLayer } from './utils/constants';

function isExternalLayer(
  layer: ExternalLayer | InternalLayer
): layer is ExternalLayer {
  return (
    'arcgisItemId' in layer || 'serviceUrl' in layer || 'itemType' in layer
  );
}

const isFolderItemVisible =
  (
    isInternalLayerVisible: (id: string) => boolean,
    isExternalLayerVisible: (id: string) => boolean
  ) =>
  (item: LayerFolderItemDto) => {
    const { layer } = item;
    return isExternalLayer(layer)
      ? isExternalLayerVisible(layer.id)
      : isInternalLayerVisible(layer.id);
  };

const areAllFolderItemsVisible = (
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean
) => {
  const { items } = group;
  if (items.length === 0) return false;
  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  return items.every(checkVisible);
};

const isFolderGroupIndeterminate = (
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean
) => {
  const { items } = group;
  if (items.length === 0) return false;
  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  const someVisible = items.some(checkVisible);
  const allVisible = items.every(checkVisible);
  return someVisible && !allVisible;
};

const handleFolderGroupVisibilityToggle = (
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean,
  toggleInternalLayerVisibility: (id: string) => void,
  toggleExternalLayerVisibility: (id: string) => void
) => {
  const { items } = group;
  if (items.length === 0) return;

  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  const allVisible = items.every(checkVisible);
  const targetVisible = !allVisible;

  items.forEach(({ layer }) => {
    const currentlyVisible = isExternalLayer(layer)
      ? isExternalLayerVisible(layer.id)
      : isInternalLayerVisible(layer.id);

    if (currentlyVisible !== targetVisible) {
      if (isExternalLayer(layer)) {
        toggleExternalLayerVisibility(layer.id);
      } else {
        toggleInternalLayerVisibility(layer.id);
      }
    }
  });
};

export function ViewLayersPanel({
  onAddLayer,
  onOpenReviewQueue,
}: {
  onAddLayer: OnAddLayer;
  onOpenReviewQueue: () => void;
}) {
  const groupGuid = useGroupGuid();
  const selectedGroup = useAppSelector(selectGroup);

  const {
    zoomToGraphic,
    zoomToExternalLayer,
    zoomToInternalLayer,
    isInternalLayerVisible,
    isExternalLayerVisible,
    toggleInternalLayerVisibility,
    toggleExternalLayerVisibility,
    aorFilterIds,
    setAorFilterIds,
    incidentFilterId,
    setIncidentFilterId,
    isArcGISAuthorized,
    setIsArcGISAuthorized,
  } = useUscgMapContext();
  const { data: aors = [] } = useAors();
  const deleteInternalLayer = useDeleteInternalLayerMutation();
  const deleteExternalLayer = useDeleteExternalLayerMutation();
  const deleteLayerNode = useDeleteLayerNodeMutation();
  const isCommandCenter = useIsCommandCenter();

  const [searchTerm, setSearchTerm] = useState('');
  const [layerDetailModal, setLayerDetailModal] =
    useState<LayerWithNodeMeta | null>(null);
  const [selectedFolderGroup, setSelectedFolderGroup] =
    useState<LayerGroupDto | null>(null);

  const { data: layerNodeGroups } = useListLayerNodes({
    filter: {
      groupGuid: groupGuid ?? '',
      ...(incidentFilterId ? { incidentIds: [incidentFilterId] } : {}),
      ...(searchTerm && { search: searchTerm }),
      ...(aorFilterIds && { aorIds: aorFilterIds }),
    },
  });

  const aorOptions = aors.map((a) => ({ value: a.id, label: a.name }));

  const renderLayerItem = (changeArg: LayerWithNodeMeta) => {
    const { layer, type } = changeArg;

    return type === 'internal' ? (
      <InternalLayerItem
        key={`internal-${layer.id}`}
        layer={layer}
        onDelete={() =>
          deleteInternalLayer.mutate({ internalLayerId: layer.id })
        }
        onOpenInNewClick={() => setLayerDetailModal(changeArg)}
        onZoomToLayer={() => zoomToInternalLayer({ internalLayerId: layer.id })}
      />
    ) : (
      <ExternalLayerItem
        key={`external-${layer.id}`}
        layer={layer}
        onDelete={() =>
          deleteExternalLayer.mutate({ externalLayerId: layer.id })
        }
        onOpenInNewClick={() => setLayerDetailModal(changeArg)}
        onZoomToLayer={() => zoomToExternalLayer({ externalLayerId: layer.id })}
        onZoomToGraphic={(graphic) => zoomToGraphic({ graphic })}
      />
    );
  };

  return (
    <div className="d-flex flex-column gap-3 px-2 pt-2 pb-5">
      <Card>
        <div className="d-flex justify-content-between">
          <StylishNewSearchBar
            placeholder="Search..."
            value={searchTerm}
            onChangeSearchTerm={(val) => setSearchTerm(val.trim())}
          />

          <div className="d-flex gap-2">
            <AddLayerFolderModal />
            <Dropdown style={{ height: '100%' }}>
              <Dropdown.Toggle
                className="button button--primary pe-auto"
                style={{ height: '100%' }}
                id="add-layer-dropdown"
              >
                <SharedIcon iconName="add" size="sm" />
                Add Layer
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item
                  onClick={() => {
                    onAddLayer('dhs');
                  }}
                >
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add from DHS</span>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    onAddLayer('external');
                  }}
                >
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add from ArcGIS</span>
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAddLayer('internal')}>
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add Internal Layer</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Card>

      <div className="row g-2">
        <div className="col-6">
          <Card>
            <IncidentSelect
              value={incidentFilterId}
              onChange={setIncidentFilterId}
              isClearable
              // incident filter is disabled in Incident Workspace
              isDisabled={!isCommandCenter}
            />
          </Card>
        </div>
        <div className="col-6">
          <Card>
            <PratusSelect
              label="AOR"
              isMulti
              options={aorOptions}
              value={
                aorFilterIds
                  ? aorOptions.filter((o) =>
                      aorFilterIds.includes(String(o.value))
                    )
                  : undefined
              }
              onChange={(val) => {
                const options = (val as OptionType[] | null) ?? [];
                if (!options.length || options.length === aorOptions.length) {
                  setAorFilterIds(null);
                  return;
                }

                setAorFilterIds(options.map((o) => o.value as string));
              }}
              isClearable
            />
          </Card>
        </div>
      </div>

      <div className="d-flex gap-2">
        <StylishNewButton
          className="button--secondary"
          disabled={isArcGISAuthorized}
          onClick={async () => {
            await registerArcGisOAuthInfo(selectedGroup);
            setIsArcGISAuthorized(true);
          }}
        >
          {isArcGISAuthorized ? (
            <>
              <span className="pe-1">ArcGIS Connected</span>
              <SharedIcon iconName="check" size="sm" color="success" />
            </>
          ) : (
            <span>Connect ArcGIS</span>
          )}
        </StylishNewButton>
        {!isCommandCenter && (
          <StylishNewButton
            className="button--secondary"
            onClick={onOpenReviewQueue}
          >
            Review queue
          </StylishNewButton>
        )}
      </div>

      <div className="d-grid gap-2 mt-2">
        {layerNodeGroups?.data.map((group: LayerGroupDto, index: number) => {
          const folderLabel = group.folder?.name ?? 'Other';
          const eventKey = group.folder?.id ?? `other-${index}`;

          return (
            <Accordion
              key={eventKey}
              defaultActiveKey={group.items.length > 0 ? eventKey : undefined}
            >
              <BSCard className="accordion--card bg-neutral-800 border border-neutral-500">
                <AccordionHeader eventKey={eventKey} hasIcon>
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <StylishNewCheckbox
                        className="mt-0"
                        checked={areAllFolderItemsVisible(
                          group,
                          isInternalLayerVisible,
                          isExternalLayerVisible
                        )}
                        indeterminate={isFolderGroupIndeterminate(
                          group,
                          isInternalLayerVisible,
                          isExternalLayerVisible
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFolderGroupVisibilityToggle(
                            group,
                            isInternalLayerVisible,
                            isExternalLayerVisible,
                            toggleInternalLayerVisibility,
                            toggleExternalLayerVisibility
                          );
                        }}
                      />
                      <span className="fw-semibold">{folderLabel}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <StylishNewButton
                        className="button--icon border-0 bg-transparent bg-bglight-hover"
                        disabled={!group.folder || group.items.length > 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!group.folder) return;

                          deleteLayerNode.mutate({
                            id: group.folder.id,
                            groupGuid: group.folder.groupGuid,
                          });
                        }}
                        data-testid="delete-layer-folder-btn"
                      >
                        <SharedIcon iconName="delete" size="sm" />
                      </StylishNewButton>
                      <StylishNewButton
                        className="button--icon border-0 bg-bglight-hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFolderGroup(group);
                        }}
                      >
                        <SharedIcon iconName="open_in_new" size="sm" />
                      </StylishNewButton>
                    </div>
                  </div>
                </AccordionHeader>
                <Accordion.Collapse eventKey={eventKey}>
                  <BSCard.Body className="p-0">
                    <ul className="list-unstyled mb-0 d-grid gap-2 p-2">
                      {group.items.length === 0 ? (
                        <li className="text-muted small">No layers</li>
                      ) : (
                        group.items.map((item) => {
                          return renderLayerItem({
                            ...(isExternalLayer(item.layer)
                              ? {
                                  type: 'external',
                                  layer: item.layer as ExternalLayer,
                                }
                              : {
                                  type: 'internal',
                                  layer: item.layer as InternalLayer,
                                }),

                            ...item,
                          });
                        })
                      )}
                    </ul>
                  </BSCard.Body>
                </Accordion.Collapse>
              </BSCard>
            </Accordion>
          );
        })}
      </div>

      <LayerDetailModal
        key={layerDetailModal?.layer.id ?? 'no-layer'}
        show={layerDetailModal !== null}
        onClose={() => setLayerDetailModal(null)}
        layerWithType={layerDetailModal}
      />
      <ViewLayerFolderModal
        show={selectedFolderGroup !== null}
        onClose={() => setSelectedFolderGroup(null)}
        folderGroup={selectedFolderGroup}
        allGroups={layerNodeGroups?.data ?? []}
      />
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-3 rounded bg-bgdark border border-neutral-500">
      {children}
    </div>
  );
}
